import { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { BRAND } from "@/lib/brand";

const links = [
    { id: "services", label: "Services" },
    { id: "process", label: "Process" },
    { id: "about", label: "About" },
    { id: "coverage", label: "Coverage" },
    { id: "book", label: "Book" },
    { id: "contact", label: "Contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <header
            data-testid="navbar"
            className={`sticky top-0 z-50 bg-[#fdfdfd] border-b hairline ${
                scrolled ? "shadow-[0_1px_0_0_rgba(10,10,10,0.1)]" : ""
            }`}
        >
            <div className="mx-auto max-w-[1400px] grid grid-cols-12 items-center">
                <a
                    href="#top"
                    data-testid="brand-logo"
                    className="col-span-8 md:col-span-3 flex items-center gap-3 px-6 py-3 border-r hairline"
                >
                    <img
                        src={BRAND.logo}
                        alt="Quiseo Auto Repair LLC"
                        className="h-12 w-12 object-contain mix-blend-multiply"
                    />
                    <div className="leading-tight">
                        <div className="font-display text-[15px] md:text-[16px] tracking-tight">
                            QUISEO
                            <span className="text-[#1d4ed8]"> AUTO REPAIR</span>
                        </div>
                        <div className="font-mono-tech text-[9px] text-[#52525b] mt-1">
                            LLC / MOBILE MECHANIC
                        </div>
                    </div>
                </a>

                <nav className="hidden md:flex col-span-6 items-center justify-center gap-7 px-6 py-5 border-r hairline">
                    {links.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-link-${l.id}`}
                            onClick={() => go(l.id)}
                            className="font-mono-tech text-[11px] text-[#0a0a0a] hover:text-[#1d4ed8] transition-colors duration-150"
                        >
                            {l.label}
                        </button>
                    ))}
                </nav>

                <div className="hidden md:flex col-span-3 items-center justify-end px-6 py-5 gap-2">
                    <a
                        href={BRAND.phoneHref}
                        data-testid="nav-phone"
                        className="hidden lg:inline-flex items-center gap-1.5 font-mono-tech text-[11px] hover:text-[#1d4ed8] transition-colors"
                    >
                        <Phone className="w-3.5 h-3.5" />
                        Call
                    </a>
                    <a
                        href={BRAND.smsHref}
                        data-testid="nav-sms"
                        className="hidden lg:inline-flex items-center gap-1.5 font-mono-tech text-[11px] hover:text-[#1d4ed8] transition-colors px-2"
                    >
                        Text
                    </a>
                    <button
                        data-testid="nav-cta-book"
                        onClick={() => go("book")}
                        className="btn-primary font-mono-tech text-[11px] px-4 py-3"
                    >
                        Book Now →
                    </button>
                </div>

                <button
                    data-testid="mobile-menu-toggle"
                    className="md:hidden col-span-4 justify-end flex items-center px-6 py-5 gap-3"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Toggle menu"
                >
                    {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            {open && (
                <div className="md:hidden border-t hairline bg-[#fdfdfd]">
                    {links.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`mobile-nav-link-${l.id}`}
                            onClick={() => go(l.id)}
                            className="w-full text-left px-6 py-4 font-mono-tech text-[12px] border-b hairline hover:bg-[#0a0a0a] hover:text-white transition-colors duration-150"
                        >
                            {l.label}
                        </button>
                    ))}
                    <button
                        onClick={() => go("book")}
                        data-testid="mobile-cta-book"
                        className="w-full btn-primary font-mono-tech text-[12px] px-6 py-4"
                    >
                        Book Now →
                    </button>
                </div>
            )}
        </header>
    );
}
